/**
 * Names of file-share locations (Task 7.14): a snapshot reads `share@<local time>` in the tab title, the tree node and
 * the breadcrumb, so a live share and its snapshot never look alike. Pure: no workspace or store imports (the Object
 * Explorer's node factories use it too).
 */
import type { StorageResourceRef } from '../../../../types/azureStorage';
import { formatStorageDateTime } from '../utils/storageDateTime';

type FileLocation = Extract<StorageResourceRef, { kind: 'share' | 'directory' | 'file' }>;

/**
 * A snapshot id (`2026-09-25T10:00:00.0000000Z`) in the ONE storage date-time format (`formatStorageDateTime`) — final
 * UI/UX batch C1 (N11): the tab title and breadcrumb read the same instant as the snapshot banner beside them (they
 * used their own `YYYY-MM-DD HH:mm:ss`). An unparsable id is shown as given.
 */
export function formatSnapshotTime(snapshot: string): string {
  const date = new Date(snapshot.replace(/(\.\d{3})\d+/u, '$1'));
  if (Number.isNaN(date.getTime())) return snapshot;
  return formatStorageDateTime(date);
}

/** `share` for the live share, `share@<local time>` for a snapshot. */
export function shareLabel(share: string, snapshot?: string): string {
  return snapshot ? `${share}@${formatSnapshotTime(snapshot)}` : share;
}

const lastSegment = (path: string): string => path.split('/').filter(Boolean).pop() ?? '';

/** Tab title of a file-share location: `share`, `share/<last folder>` or `share@<time>/<last folder>`. */
export function fileTabTitle(ref: FileLocation): string {
  const share = shareLabel(ref.share, ref.snapshot);
  if (ref.kind === 'share' || !ref.path) return share;
  return `${share}/${lastSegment(ref.path)}`;
}
